import { Component, Input, Output, EventEmitter, HostListener } from '@angular/core';
import { AgentHubItem } from '../../models';

/**
 * Agent Info Popup Component
 * 
 * A reusable modal popup that displays detailed information about an agent.
 * Shows agent details including about, version notes, and features.
 * 
 * Features:
 * - Modal overlay with backdrop
 * - Close on backdrop click or X button
 * - Agent icon and name header
 * - "Use" button and "Make it default agent" link
 * - About section with description
 * - Version section with bullet points
 * - Features section with bullet points
 * 
 * Usage:
 * <app-agent-info-popup
 *   [agent]="selectedAgent"
 *   [isVisible]="showPopup"
 *   (closed)="onPopupClosed()"
 *   (useAgent)="onUseAgent($event)"
 *   (makeDefault)="onMakeDefault($event)">
 * </app-agent-info-popup>
 */
@Component({
  selector: 'app-agent-info-popup',
  templateUrl: './agent-info-popup.component.html',
  styleUrls: ['./agent-info-popup.component.scss']
})
export class AgentInfoPopupComponent {
  /**
   * Agent data to display in the popup
   */
  @Input() agent: AgentHubItem | null = null;

  /**
   * Controls popup visibility
   */
  @Input() 
  set isVisible(value: boolean) {
    this._isVisible = value;
  }
  get isVisible(): boolean {
    return this._isVisible;
  }
  private _isVisible: boolean = false;

  /**
   * Emits when popup is closed
   */
  @Output() closed = new EventEmitter<void>();

  /**
   * Emits agent ID when "Use" button is clicked
   */
  @Output() useAgent = new EventEmitter<string>();

  /**
   * Emits agent ID when "Make it default agent" is clicked
   */
  @Output() makeDefault = new EventEmitter<string>();

  /**
   * Close popup when clicking on backdrop
   * 
   * @param event - Mouse event
   */
  onBackdropClick(event: MouseEvent): void {
    // Only close if clicking directly on the backdrop, not the modal content
    if ((event.target as HTMLElement).classList.contains('popup-overlay')) {
      this.close();
    }
  }

  /** Handle keydown on overlay (Escape to close). */
  onOverlayKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.close();
    }
  }

  /**
   * Close popup when pressing Escape key
   * 
   * @param event - Keyboard event
   */
  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKey(event: KeyboardEvent): void {
    if (this.isVisible) {
      this.close();
    }
  }

  /**
   * Emit close event
   */
  close(): void {
    this.closed.emit();
  }

  /**
   * Handle "Use" button click
   */
  onUseClick(): void {
    if (this.agent) {
      this.useAgent.emit(this.agent.id);
      this.close();
    }
  }

  /**
   * Handle "Make it default agent" link click
   */
  onMakeDefaultClick(): void {
    if (this.agent) {
      this.makeDefault.emit(this.agent.id);
    }
  }

  /**
   * Get the icon source with fallback
   * 
   * @returns string - Icon path or fallback
   */
  get iconSrc(): string {
    return this.agent?.icon || 'assets/icons/artificial-intelligence.svg';
  }

  /**
   * Get about text split into paragraphs
   * 
   * @returns string[] - Array of paragraphs
   */
  get aboutParagraphs(): string[] {
    if (!this.agent?.about) {
      return [];
    }
    return this.agent.about.split('\n').filter(p => p.trim());
  }
}
